import { useState, useEffect, useContext, useMemo } from 'react';
import _ from 'underscore';
import _preferencesService from '@bingads-webui-component-legacy/preference/service/index';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useWizardService, getFlowFactors } from '@bingads-webui-react/wizard';
import { I18nContext } from '@bingads-webui-react/i18n-context';

const buildSchemaData = ({
  data,
  timeDisplayType,
  serverContext,
  cid,
  aid,
  i18n,
}) => {
  const locations = _.map(
    _.filter(
      data.campaign && data.campaign.locationTargets && data.campaign.locationTargets.currentLevelSettings.currentLocations,
      loc => loc.SelectionStatus === 'Included'
    ),
    loc => loc.location
  );

  return ({
    campaignName: {
      data: data.campaign.Name,
    },
    assetGroupName: {
      data: data.assetGroup.data.Name,
    },
    appName: {
      data: data.campaign.AppSetting.name,
    },
    appPlatform: {
      data: data.campaign.AppSetting.AppPlatform,
    },
    autoGeneratedAssets: {
      data: {
        finalUrlExpansionOptOut: data.campaign.finalUrlExpansionOptOut,
        finalUrlExpansions: data.campaign.finalUrlExpansions,
        autoGeneratedTextOptOut: data.campaign.autoGeneratedTextOptOut,
        autoGeneratedImageOptOut: data.campaign.autoGeneratedImageOptOut,
      },
    },
    budget: {
      data: data.campaign.Budget,
      currencyCode: serverContext.CurrentAccount.CurrencyCode,
    },
    bidStrategy: {
      data: data.campaign.BiddingScheme,
    },
    bidValue: {
      data: data.campaign.BiddingScheme.ManualCPC,
      currencyCode: serverContext.CurrentAccount.CurrencyCode,
    },
    costPerSale: {
      data: {
        costPerSaleOptOut: data.campaign.costPerSaleOptOut,
      },
    },
    conversionGoals: {
      conversionGoals: data.campaign.conversionGoals,
    },
    locationTargeting: {
      locations,
      targetOption: data.campaign && data.campaign.locationTargets && data.campaign.locationTargets.currentLevelSettings.targetOption,
    },
    languages: {
      data: data.campaign.Languages,
    },
    audienceSignal: {
      data: data.assetGroup.data.AudienceGroup,
    },
    searchThemes: {
      data: data.assetGroup.data.AssetGroupSearchThemes,
    },
    images: {
      data: data.assetGroup.data.Images,
      shouldFetchFromMT: true,
      cid,
      aid,
    },
    logos: {
      data: data.assetGroup.data.Images,
      shouldFetchFromMT: true,
      cid,
      aid,
      label: _TL_('Logos'),
      isLogo: true,
    },
    headlines: {
      data: data.assetGroup.data.Headlines,
    },
    descriptions: {
      data: data.assetGroup.data.Descriptions,
    },
    videos: {
      data: data.assetGroup.data.videos,
    },
    adScheduleCampaign: {
      startDate: data.assetGroup.data.StartDate,
      endDate: data.assetGroup.data.EndDate,
      adSchedules: data.campaign && data.campaign.adSchedules && data.campaign.adSchedules.dayTimeRanges,
      useSearcherTimeZone: data.campaign.AdScheduleUseSearcherTimeZone,
      timeDisplayType,
    },
    urlOptions: {
      data: {
        TrackingUrlTemplate: data.campaign.TrackingUrlTemplate,
        FinalUrlSuffix: data.campaign.FinalUrlSuffix,
        UrlCustomParameters: data.campaign.UrlCustomParameters,
      },
    },
    finalUrlExpansions: {
      data: {
        finalUrlExpansions: data.campaign.finalUrlExpansions,
      },
    },
    urlRules: {
      data: {
        i18n,
        finalUrls: data.assetGroup.data.FinalUrls,
        path1: data.assetGroup.data.Path1,
        path2: data.assetGroup.data.Path2,
        mobileFinalUrls: data.assetGroup.data.MobileFinalUrls,
        urlRules: data.assetGroup.data.AssetGroupUrlTargets,
      },
    },
    newCustomerAcquisition: {
      data: {
        newCustomerAcquisitionOptOut: data.campaign.newCustomerAcquisitionOptOut,
        newCustomerAcquisition: data.campaign.newCustomerAcquisition,
      },
      currencyCode: serverContext.CurrentAccount.CurrencyCode,
    },
  });
};

export const useSummarySchemaData = ({ data }) => {
  const { scope } = useContext(ScopeContext) || {};

  const i18n = useContext(I18nContext);

  const { cid, aid } = scope.values;

  const [timeDisplayType, setTimeDisplayType] = useState('12-hour');

  const serverContext = useContext(ServerContext);
  const { Permissions, DynamicPermissions } = serverContext || {};
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const { state } = useWizardService();

  const { loadedStoresData, savedProductFilter, savedListingGroup } = state.context.wizardData;

  const { flow } = getFlowFactors(state);

  useEffect(() => {
    const preferencesService = _preferencesService.default || _preferencesService;
    preferencesService.initialize().then(() => {
      const isTwelveHourDisplay = preferencesService.findByName('WebApp.CampaignUI.isTwelveHourDisplay') || {};
      setTimeDisplayType(_.isUndefined(isTwelveHourDisplay.Data) || isTwelveHourDisplay.Data ? '12-hour' : '24-hour');
    });
  }, []);

  return (data && data.campaign && data.assetGroup && buildSchemaData({
    data,
    timeDisplayType,
    serverContext,
    cid,
    aid,
    flow,
    loadedStoresData,
    savedProductFilter,
    savedListingGroup,
    i18n,
    permissions,
  })) || {};
};
